import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@connectrpc/connect-query'
import { useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { adminListOrganizationBilling } from '@/gen/cortado/v1/admin-AdminService_connectquery'
import type { OrgBillingSummary } from '@/gen/cortado/v1/admin_pb'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CccTrialInviteButton } from '@/components/billing/ccc-trial-invite-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  SortableHeader,
  compareNumbers,
  compareStrings,
  useTableSort,
} from '@/components/ui/sortable-header'
import {
  BILLING_STATE_OPTIONS,
  PLAN_KEY_OPTIONS,
  billingIntervalLabel,
  billingStateBadgeVariant,
  billingStateLabel,
  formatDate,
  formatUsdCents,
  planKeyLabel,
} from '@/lib/billing'
import {
  ORG_BILLING_OVERVIEW_PAGE_SIZE,
  buildOrgBillingOverviewRequest,
  orgBillingOverviewRangeLabel,
  orgBillingPagerState,
} from './-pagination'

type BillingSortField = 'organization' | 'state' | 'plan' | 'seats' | 'mrr' | 'firstCharge'

export const Route = createFileRoute('/_admin/billing/')({
  component: BillingOverviewPage,
})

const COLUMN_COUNT = 6

function mrrForRow(row: OrgBillingSummary): bigint {
  return row.monthlyRecurringRevenueCents
}

export function BillingOverviewPage() {
  const [stateFilter, setStateFilter] = useState<string>('all')
  const [planFilter, setPlanFilter] = useState<string>('all')
  const [pageIndex, setPageIndex] = useState(0)
  const { sortField, sortDirection, toggleSort } = useTableSort<BillingSortField>('organization')

  const request = useMemo(
    () =>
      buildOrgBillingOverviewRequest({
        stateFilter,
        planFilter,
        pageIndex,
      }),
    [stateFilter, planFilter, pageIndex],
  )

  const { data, isLoading, isFetching, isError, error } = useQuery(
    adminListOrganizationBilling,
    request,
  )

  const sortedRows = useMemo(() => {
    const rows = data?.organizations ?? []
    const dir = sortDirection === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => {
      switch (sortField) {
        case 'organization':
          return compareStrings(a.organizationName, b.organizationName) * dir
        case 'state':
          return compareNumbers(a.state, b.state) * dir
        case 'plan':
          return compareNumbers(a.assignedPlanKey, b.assignedPlanKey) * dir
        case 'seats':
          return compareNumbers(a.billableSeats, b.billableSeats) * dir
        case 'mrr':
          return compareNumbers(mrrForRow(a), mrrForRow(b)) * dir
        case 'firstCharge':
          return compareNumbers(a.firstChargeAt?.seconds ?? 0n, b.firstChargeAt?.seconds ?? 0n) * dir
        default:
          return 0
      }
    })
  }, [data?.organizations, sortField, sortDirection])

  const totalCount = data?.totalCount ?? 0
  const { prevDisabled, nextDisabled } = orgBillingPagerState({
    pageIndex,
    nextPageToken: data?.nextPageToken,
    isFetching,
  })
  const rangeLabel = orgBillingOverviewRangeLabel({
    pageIndex,
    pageSize: ORG_BILLING_OVERVIEW_PAGE_SIZE,
    rowCount: sortedRows.length,
    totalCount,
  })

  const resetPaging = () => setPageIndex(0)

  if (isError) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Billing</h1>
          <p className="text-muted-foreground text-sm">
            {error instanceof Error ? error.message : 'Unable to load billing overview'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Billing</h1>
          <p className="text-muted-foreground text-sm">
            Org billing/access state, plan, seats, and MRR across every organization
          </p>
        </div>
        <CccTrialInviteButton />
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Select
          value={stateFilter}
          onValueChange={(value) => {
            setStateFilter(value)
            resetPaging()
          }}
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Filter by state" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All states</SelectItem>
            {BILLING_STATE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={String(o.value)}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={planFilter}
          onValueChange={(value) => {
            setPlanFilter(value)
            resetPaging()
          }}
        >
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Filter by plan" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All plans</SelectItem>
            {PLAN_KEY_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={String(o.value)}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <SortableHeader
                  active={sortField === 'organization'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('organization')}
                >
                  Organization
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'state'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('state')}
                >
                  State
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'plan'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('plan')}
                >
                  Plan / interval
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'seats'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('seats')}
                >
                  Seats
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'mrr'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('mrr')}
                >
                  <span title="Includes trialing, grace, and paused-payment orgs at full booked value - not collected revenue. Comp accounts are excluded.">
                    Booked MRR
                  </span>
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'firstCharge'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('firstCharge')}
                >
                  First charge
                </SortableHeader>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="text-center text-muted-foreground py-8">
                  Loading billing overview...
                </TableCell>
              </TableRow>
            ) : !sortedRows.length ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="text-center text-muted-foreground py-8">
                  {stateFilter !== 'all' || planFilter !== 'all'
                    ? 'No organizations match your filters'
                    : 'No organizations found'}
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map((row) => (
                <TableRow key={row.organizationId}>
                  <TableCell>
                    <Link
                      to="/billing/$organizationId"
                      params={{ organizationId: row.organizationId }}
                      className="font-medium hover:underline"
                    >
                      {row.organizationName}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Badge variant={billingStateBadgeVariant(row.state)}>
                      {billingStateLabel(row.state)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div>{planKeyLabel(row.assignedPlanKey)}</div>
                    <div className="text-muted-foreground text-xs">
                      {billingIntervalLabel(row.selectedInterval)}
                    </div>
                  </TableCell>
                  <TableCell>{row.billableSeats}</TableCell>
                  <TableCell className="whitespace-nowrap">{formatUsdCents(mrrForRow(row))}</TableCell>
                  <TableCell className="text-muted-foreground">{formatDate(row.firstChargeAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted-foreground">{rangeLabel}</p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPageIndex((index) => Math.max(0, index - 1))}
            disabled={prevDisabled}
          >
            <ChevronLeft className="h-3.5 w-3.5 mr-1" />
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPageIndex((index) => index + 1)}
            disabled={nextDisabled}
          >
            Next
            <ChevronRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  )
}
